import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { StorefrontService } from "src/app/services/StorefrontService";
import { Basket, money, ProductType } from "./Basket";
import { clubTheme } from "./ClubTheme";
import { sanitiseSvg } from "./SanitiseSvg";
import "./Shop.css";
import { ShopHeader } from "./ShopHeader";
import { ShopNav } from "./ShopNav";

type Chosen = { seatId: number; label: string; pricePence: number; ticketTypeId: number };

/**
 * Pick a seat off the club's own plan of the ground.
 *
 * The SVG is drawn by the club — our seat model records which block a seat is
 * in, never where it is — so blocks are matched to elements inside it by id
 * and coloured by what's left.
 *
 * It is inlined rather than shown in an <img> because colouring a stand and
 * knowing which one was tapped are both impossible inside an image. That means
 * it must be sanitised first: an SVG can carry scripts. See SanitiseSvg.
 *
 * Tapping a stand opens its seats as rows, which is how somebody actually
 * chooses — "two together in row C", not a pinch-zoom hunt for seat 4712 on a
 * phone.
 */
export function ShopSeatMap() {
	const { slug = "", id = "" } = useParams();
	const navigate = useNavigate();

	const [map, setMap] = React.useState<any>(null);
	const [svg, setSvg] = React.useState<string | null>(null);
	const [block, setBlock] = React.useState<any>(null);
	const [chosen, setChosen] = React.useState<Chosen[]>([]);
	const [ticketTypeId, setTicketTypeId] = React.useState<number | null>(null);
	const [loading, setLoading] = React.useState(true);
	const [error, setError] = React.useState<string | null>(null);
	const holder = React.useRef<HTMLDivElement>(null);

	React.useEffect(() => {
		let live = true;
		(async () => {
			try {
				const res = await StorefrontService.getSeatMap(slug, Number(id));
				if (!res.ok) return;
				const body = await res.json();
				if (!live) return;
				setMap(body);

				if (body.hasMap && body.svgUrl) {
					const drawing = await fetch(body.svgUrl);
					if (drawing.ok && live) setSvg(sanitiseSvg(await drawing.text()));
				}
			} catch {
				if (live) setError("We couldn't load the seating plan.");
			} finally {
				if (live) setLoading(false);
			}
		})();
		return () => { live = false; };
	}, [slug, id]);

	// Colour each stand by what's left, and make it tappable.
	React.useEffect(() => {
		const root = holder.current;
		if (!root || !svg || !map?.blocks) return;

		// Listeners are tracked so they can be taken off again. Without that,
		// every re-render stacks another one and a single tap opens the block
		// several times over.
		const attached: { el: SVGElement; fn: () => void }[] = [];

		for (const b of map.blocks) {
			if (!b.svgElementId) continue;
			const el = root.querySelector(`#${CSS.escape(b.svgElementId)}`) as SVGElement | null;
			if (!el) continue;

			const soldOut = b.available === 0;
			el.setAttribute("fill", soldOut ? "#374151" : "var(--shop-accent, #11B981)");
			el.setAttribute("fill-opacity", soldOut ? "0.5" : "0.85");
			el.setAttribute("stroke", "#0f1523");
			el.setAttribute("stroke-width", "2");
			el.style.cursor = soldOut ? "not-allowed" : "pointer";

			// Screen readers get the same information the colour carries.
			el.setAttribute("role", "button");
			el.setAttribute("aria-label",
				`${b.blockName}, ${soldOut ? "sold out" : `${b.available} of ${b.total} seats available`}`);

			if (soldOut) continue;

			const fn = () => openBlock(b.blockId);
			el.addEventListener("click", fn);
			attached.push({ el, fn });
		}

		return () => attached.forEach(({ el, fn }) => el.removeEventListener("click", fn));
	});

	const openBlock = async (blockId: number) => {
		try {
			const res = await StorefrontService.getBlockSeats(slug, Number(id), blockId);
			if (!res.ok) return;
			const body = await res.json();
			setBlock(body);
			setTicketTypeId(body.ticketTypes?.[0]?.ticketTypeId ?? null);
		} catch {
			setError("We couldn't load those seats.");
		}
	};

	const toggle = (seat: any, rowLabel: string) => {
		if (!seat.available || !ticketTypeId) return;
		const type = block.ticketTypes.find((t: any) => t.ticketTypeId === ticketTypeId);
		if (!type) return;

		setChosen((current) => {
			const already = current.find((c) => c.seatId === seat.seatId);
			if (already) return current.filter((c) => c.seatId !== seat.seatId);
			return [...current, {
				label: `${block.blockName} ${rowLabel}${seat.seatNumber}`,
				pricePence: type.pricePence,
				seatId: seat.seatId,
				ticketTypeId
			}];
		});
	};

	const addToBasket = () => {
		if (!chosen.length) return;

		// One line per ticket type — the price differs, and the server prices
		// each line on its own.
		const byType = new Map<number, Chosen[]>();
		for (const seat of chosen) {
			byType.set(seat.ticketTypeId, [...(byType.get(seat.ticketTypeId) || []), seat]);
		}

		for (const [type, seats] of Array.from(byType.entries())) {
			const name = block?.ticketTypes?.find((t: any) => t.ticketTypeId === type)?.name || "Ticket";
			Basket.add(slug, {
				name: `${name} — ${seats.map((s) => s.label).join(", ")}`,
				occasion: map?.venueName || "Reserved seat",
				pricePence: seats[0].pricePence,
				productId: Number(id),
				productType: ProductType.Fixture,
				quantity: seats.length,
				seatIds: seats.map((s) => s.seatId),
				ticketTypeId: type,
				when: ""
			});
		}
		navigate(`/club/${slug}/basket`);
	};

	if (loading) {
		return <div className="shop has-nav"><div className="shop-result"><span className="shop-spinner" /></div></div>;
	}

	if (!map?.hasMap) {
		return (
			<div className="shop has-nav" style={clubTheme(map?.club)}>
				<ShopHeader club={map?.club} slug={slug} />
				<div className="shop-result">
					<h2>No seating plan</h2>
					<p>This ground is general admission &mdash; any seat, first come first served.</p>
					<button className="shop-btn" onClick={() => navigate(`/club/${slug}/fixture/${id}`)} type="button">
						Get tickets
					</button>
				</div>
				<ShopNav slug={slug} />
			</div>
		);
	}

	const total = chosen.reduce((sum, c) => sum + c.pricePence, 0);

	return (
		<div className="shop has-nav" style={clubTheme(map?.club)}>
			<ShopHeader club={map?.club} slug={slug} />
			<div className="shop-inner">
				<h2 className="shop-section-title">{map.venueName || "Choose your seats"}</h2>

				{error && <div className="shop-error">{error}</div>}

				<div className="shop-map" dangerouslySetInnerHTML={{ __html: svg || "" }} ref={holder} />

				<div className="shop-map-key">
					<span><i className="free" /> Available</span>
					<span><i className="gone" /> Sold out</span>
				</div>

				{!block && <p className="shop-note">Tap a stand to pick your seats.</p>}

				{block && <>
					<h2 className="shop-section-title">{block.blockName}</h2>

					<div className="shop-tabs">
						{block.ticketTypes.map((t: any) => (
							<button
								className={ticketTypeId === t.ticketTypeId ? "on" : ""}
								key={t.ticketTypeId}
								onClick={() => setTicketTypeId(t.ticketTypeId)}
								type="button"
							>
								{t.name} {money(t.pricePence)}
							</button>
						))}
					</div>

					{block.rows.map((row: any) => (
						<div className="shop-row" key={row.rowLabel}>
							<span className="shop-row-label">{row.rowLabel}</span>
							<div className="shop-row-seats">
								{row.seats.map((seat: any) => {
									const picked = chosen.some((c) => c.seatId === seat.seatId);
									return (
										<button
											aria-label={`Row ${row.rowLabel} seat ${seat.seatNumber}${seat.available ? "" : ", taken"}`}
											className={`shop-seat${picked ? " picked" : ""}${seat.available ? "" : " gone"}${seat.accessible ? " accessible" : ""}`}
											disabled={!seat.available}
											key={seat.seatId}
											onClick={() => toggle(seat, row.rowLabel)}
											type="button"
										>
											{seat.seatNumber}
										</button>
									);
								})}
							</div>
						</div>
					))}

					<p className="shop-note">
						<span className="shop-seat accessible inline" /> marks an accessible space.
					</p>
				</>}

				{chosen.length > 0 &&
					<div className="shop-sticky">
						<button className="shop-btn" onClick={addToBasket} type="button">
							{chosen.length} {chosen.length === 1 ? "seat" : "seats"} &middot; {money(total)}
						</button>
					</div>}
			</div>
			<ShopNav slug={slug} />
		</div>
	);
}
